"use client";

import { useEffect, useRef, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { ThumbsUp, ThumbsDown, Plus, X, Check } from "lucide-react";
import type { SuggestionKind, SuggestionWithVotes } from "@/lib/community-tags";

interface CommunityTagsSectionProps {
  essenceId: string;
  isAuthenticated: boolean;
  canModerate: boolean;
}

async function fetchSuggestions(essenceId: string): Promise<{ suggestions: SuggestionWithVotes[] }> {
  const response = await fetch(`/api/community/suggestions?essenceId=${essenceId}`);
  if (!response.ok) throw new Error("Failed to load suggestions");
  return response.json();
}

async function searchValues(kind: SuggestionKind, query: string, essenceId: string) {
  const response = await fetch(
    `/api/community/suggest-values?kind=${kind}&query=${encodeURIComponent(query)}&essenceId=${essenceId}`
  );
  if (!response.ok) throw new Error("Failed to search values");
  const data = await response.json();
  return data.values as string[];
}

export function CommunityTagsSection({
  essenceId,
  isAuthenticated,
  canModerate,
}: CommunityTagsSectionProps) {
  const queryClient = useQueryClient();
  const [kind, setKind] = useState<SuggestionKind>("keyword");
  const [value, setValue] = useState("");
  const [autocomplete, setAutocomplete] = useState<string[]>([]);
  const [open, setOpen] = useState(false);
  const searchSeq = useRef(0);
  const searchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (searchTimer.current) clearTimeout(searchTimer.current);
    };
  }, []);

  const { data, isLoading } = useQuery({
    queryKey: ["community-suggestions", essenceId],
    queryFn: () => fetchSuggestions(essenceId),
  });

  const suggestions = data?.suggestions ?? [];
  const pendingSuggestions = suggestions.filter((s) => s.status === "pending");
  const approvedSuggestions = suggestions.filter((s) => s.status === "approved");

  const createMutation = useMutation({
    mutationFn: () =>
      fetch("/api/community/suggestions", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ essenceId, kind, value }),
      }),
    onSuccess: () => {
      toast.success("Suggestion submitted");
      setValue("");
      queryClient.invalidateQueries({ queryKey: ["community-suggestions", essenceId] });
      queryClient.invalidateQueries({ queryKey: ["moderation", "suggestions"] });
    },
    onError: (error: any) => {
      toast.error(error.message || "Failed to submit suggestion");
    },
  });

  const voteMutation = useMutation({
    mutationFn: ({ suggestionId, vote }: { suggestionId: number; vote: 1 | -1 }) =>
      fetch(`/api/community/suggestions/${suggestionId}/vote`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ vote }),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["community-suggestions", essenceId] });
    },
    onError: (error: any) => {
      toast.error(error.message || "Failed to vote");
    },
  });

  const removeVoteMutation = useMutation({
    mutationFn: (suggestionId: number) =>
      fetch(`/api/community/suggestions/${suggestionId}/vote`, { method: "DELETE" }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["community-suggestions", essenceId] });
    },
    onError: (error: any) => {
      toast.error(error.message || "Failed to remove vote");
    },
  });

  const approveMutation = useMutation({
    mutationFn: (suggestionId: number) =>
      fetch(`/api/moderation/suggestions/${suggestionId}/approve`, { method: "POST" }),
    onSuccess: () => {
      toast.success("Suggestion approved");
      queryClient.invalidateQueries({ queryKey: ["community-suggestions", essenceId] });
      queryClient.invalidateQueries({ queryKey: ["moderation", "suggestions"] });
    },
    onError: (error: any) => {
      toast.error(error.message || "Failed to approve");
    },
  });

  const rejectMutation = useMutation({
    mutationFn: (suggestionId: number) =>
      fetch(`/api/moderation/suggestions/${suggestionId}/reject`, { method: "POST" }),
    onSuccess: () => {
      toast.success("Suggestion rejected");
      queryClient.invalidateQueries({ queryKey: ["community-suggestions", essenceId] });
      queryClient.invalidateQueries({ queryKey: ["moderation", "suggestions"] });
    },
    onError: (error: any) => {
      toast.error(error.message || "Failed to reject");
    },
  });

  const editMutation = useMutation({
    mutationFn: ({ suggestionId, value }: { suggestionId: number; value: string }) =>
      fetch(`/api/moderation/suggestions/${suggestionId}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ value }),
      }),
    onSuccess: () => {
      toast.success("Suggestion edited");
      queryClient.invalidateQueries({ queryKey: ["community-suggestions", essenceId] });
    },
    onError: (error: any) => {
      toast.error(error.message || "Failed to edit");
    },
  });

  const handleSearch = (query: string) => {
    if (searchTimer.current) clearTimeout(searchTimer.current);
    if (query.length < 2) {
      searchSeq.current++;
      setAutocomplete([]);
      return;
    }
    searchTimer.current = setTimeout(async () => {
      const seq = ++searchSeq.current;
      const results = await searchValues(kind, query, essenceId);
      // Ignore stale responses if the user kept typing.
      if (seq === searchSeq.current) {
        setAutocomplete(results);
      }
    }, 200);
  };

  const handleVote = (suggestionId: number, currentVote: number | null, newVote: 1 | -1) => {
    if (currentVote === newVote) {
      removeVoteMutation.mutate(suggestionId);
    } else {
      voteMutation.mutate({ suggestionId, vote: newVote });
    }
  };

  const handleEdit = (suggestion: SuggestionWithVotes) => {
    const newValue = prompt("Edit suggestion value:", suggestion.value);
    if (newValue && newValue !== suggestion.value) {
      editMutation.mutate({ suggestionId: suggestion.id, value: newValue });
    }
  };

  return (
    <section className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold">Community tags</h2>
        {canModerate && (
          <Badge variant="neutral" className="text-xs">
            Moderator
          </Badge>
        )}
      </div>

      {approvedSuggestions.length > 0 && (
        <div className="space-y-2">
          <h3 className="text-sm font-medium text-ink-3">Approved</h3>
          <div className="flex flex-wrap gap-2">
            {approvedSuggestions.map((s) => (
              <Badge key={s.id} variant="neutral">
                {s.kind}: {s.value}
              </Badge>
            ))}
          </div>
        </div>
      )}

      <div className="space-y-2">
        <h3 className="text-sm font-medium text-ink-3">Pending suggestions</h3>
        {pendingSuggestions.length === 0 ? (
          <p className="text-sm text-ink-3">No pending suggestions.</p>
        ) : (
          <div className="space-y-2">
            {pendingSuggestions.map((s) => (
              <div
                key={s.id}
                className="flex flex-col gap-2 rounded-md border border-line p-3 sm:flex-row sm:items-center sm:justify-between"
              >
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <Badge variant="neutral">{s.kind}</Badge>
                    <span className="font-medium">{s.value}</span>
                    {s.originalValue && s.originalValue !== s.value && (
                      <Badge
                        variant="neutral"
                        className="cursor-help text-xs"
                        title={`Original: ${s.originalValue}`}
                      >
                        edited
                      </Badge>
                    )}
                  </div>
                  <p className="mt-1 text-xs text-ink-3">
                    Suggested by {s.suggestedByName ?? s.suggestedByEmail ?? "unknown"}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <div className="flex items-center gap-1 text-sm text-ink-3">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7"
                      onClick={() => isAuthenticated && handleVote(s.id, s.userVote, 1)}
                      disabled={!isAuthenticated}
                      title={isAuthenticated ? "Upvote" : "Log in to vote"}
                    >
                      <ThumbsUp
                        className={`h-4 w-4 ${s.userVote === 1 ? "fill-accent text-accent" : ""}`}
                      />
                    </Button>
                    <span className="w-6 text-center">{s.upvotes - s.downvotes}</span>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7"
                      onClick={() => isAuthenticated && handleVote(s.id, s.userVote, -1)}
                      disabled={!isAuthenticated}
                      title={isAuthenticated ? "Downvote" : "Log in to vote"}
                    >
                      <ThumbsDown
                        className={`h-4 w-4 ${s.userVote === -1 ? "fill-danger text-danger" : ""}`}
                      />
                    </Button>
                  </div>
                  {canModerate && (
                    <div className="flex items-center gap-1">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => approveMutation.mutate(s.id)}
                      >
                        <Check className="h-3 w-3" />
                      </Button>
                      <Button
                        variant="danger"
                        size="sm"
                        onClick={() => rejectMutation.mutate(s.id)}
                      >
                        <X className="h-3 w-3" />
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => handleEdit(s)}>
                        Edit
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {isAuthenticated && (
        <div className="space-y-2 rounded-md border border-line p-3">
          <h3 className="text-sm font-medium">Suggest a tag</h3>
          <div className="flex flex-col gap-2 sm:flex-row">
            <Select
              value={kind}
              onValueChange={(v) => {
                const nextKind = v as SuggestionKind;
                setKind(nextKind);
                // Re-run autocomplete against the new vocabulary.
                if (value.length >= 2) {
                  const seq = ++searchSeq.current;
                  searchValues(nextKind, value, essenceId).then((results) => {
                    if (seq === searchSeq.current) setAutocomplete(results);
                  });
                } else {
                  setAutocomplete([]);
                }
              }}
            >
              <SelectTrigger className="w-full sm:w-32">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="category">Category</SelectItem>
                <SelectItem value="trait">Trait</SelectItem>
                <SelectItem value="keyword">Keyword</SelectItem>
              </SelectContent>
            </Select>
            <div className="relative flex-1">
              <Input
                placeholder="Type to search or create..."
                value={value}
                onChange={(e) => {
                  setValue(e.target.value);
                  handleSearch(e.target.value);
                  setOpen(true);
                }}
                onFocus={() => {
                  if (value.length >= 2) setOpen(true);
                }}
                onBlur={() => {
                  // Delay so suggestion clicks (onMouseDown) fire first.
                  setTimeout(() => setOpen(false), 150);
                }}
              />
              {open && value.length >= 2 && (
                <div className="surface-float absolute left-0 right-0 top-full z-50 mt-1 max-h-56 overflow-y-auto rounded-md p-1">
                  {autocomplete.length === 0 ? (
                    <p className="px-2 py-1 text-sm text-ink-3">
                      No matches. Your input will be submitted as a new suggestion.
                    </p>
                  ) : (
                    <div className="space-y-1">
                      {autocomplete.map((item) => (
                        <button
                          key={item}
                          type="button"
                          className="w-full rounded px-2 py-1 text-left text-sm hover:bg-surface-2"
                          onMouseDown={(e) => {
                            e.preventDefault();
                            setValue(item);
                            setOpen(false);
                          }}
                        >
                          {item}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
            <Button
              onClick={() => createMutation.mutate()}
              disabled={!value.trim() || createMutation.isPending}
            >
              <Plus className="mr-1 h-4 w-4" />
              Suggest
            </Button>
          </div>
          <p className="text-xs text-ink-3">
            Accounts must be at least 1 hour old to suggest or vote.
          </p>
        </div>
      )}
    </section>
  );
}
